import React, { useMemo, useState } from 'react'
import { useDebounce } from '@node-space/hooks'
import { Avatar, ComboboxItem } from '@node-space/ui-components'
import { useGetLedgerWallets } from 'hooks/queries/Ledger/useGetLedgerWallets'

interface UseWalletsOptionsProps {
  selectedCurrency: string
  selectedWalletId: string
}

export const useRewardsWalletsOptions = ({
  selectedCurrency,
  selectedWalletId,
}: UseWalletsOptionsProps) => {
  const [searchRewardsWallets, setSearchRewardsWallets] = useState('')
  const debouncedSearchRewardsWallets = useDebounce(searchRewardsWallets, 500)

  const { data: rewardsWalletsData, isLoading: isLoadingRewardsWallets } = useGetLedgerWallets({
    currencyCode: selectedCurrency,
    searchTerm: debouncedSearchRewardsWallets,
  })

  const rewardsWallets = rewardsWalletsData?.content || []

  const rewardsWalletsOptions = useMemo<ComboboxItem[]>(() => {
    if (!rewardsWallets || !selectedCurrency) return []

    return rewardsWallets.map(wallet => ({
      value: wallet.id,
      leftText: wallet.name,
      LeftElement: <Avatar name={wallet.balance.currencyCode} size="small" />,
      rightText: `${wallet.balance.value} ${wallet.balance.currencyCode}`,
    }))
  }, [rewardsWallets, selectedCurrency])

  const selectedWallet = useMemo(() => {
    if (!selectedWalletId || !rewardsWalletsOptions.length) return null
    return rewardsWalletsOptions.find(wallet => wallet.value === selectedWalletId)
  }, [selectedWalletId, rewardsWalletsOptions])

  const onSearchChange = (search: string) => {
    setSearchRewardsWallets(search)
  }

  return {
    rewardsWallets,
    rewardsWalletsOptions,
    selectedWallet,
    onSearchChange,
    isLoading: isLoadingRewardsWallets,
  }
}
